import { type SubBranchListItem, type SubBranchListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { SubBranchFormDrawer } from '../components/SubBranchFormDrawer';
import { listSubBranches } from '../sub-branches.api';
import {
  useBulkDeleteSubBranches,
  useBulkSetSubBranchesStatus,
  useDeleteSubBranch,
  useSubBranchesList,
} from '../use-sub-branches';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCanExportFinanceData, useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const SubBranchesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canExportFinance = useCanExportFinanceData();
  const canDelete = me?.role === 'ADMIN';
  const deleteSubBranch = useDeleteSubBranch();
  const bulkDeleteMut = useBulkDeleteSubBranches();
  const bulkSetStatusMut = useBulkSetSubBranchesStatus();

  const columns: ListScreenColumns<SubBranchListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<SubBranchListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'branch',
        header: 'Branch',
        cell: (row) => <span className="text-zinc-600">{row.branch?.name ?? '-'}</span>,
      },
      {
        id: 'address',
        header: 'Address',
        cell: (row) => <span className="text-zinc-600">{row.address ?? '-'}</span>,
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        sortable: true,
        width: '80px',
        cell: (row) => <span>{row.sortOrder}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<SubBranchListItem, SubBranchListQuery>
      title="Sub-branches"
      description="Finer-grained office units a user can be tagged with alongside their branch."
      useList={useSubBranchesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add sub-branch' }}
      renderDrawer={({ open, mode, row, close }) => (
        <SubBranchFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          subBranch={row}
        />
      )}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteSubBranch.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this sub-branch?',
            description: `${row.name} will be soft-deleted and hidden from lists. Users already assigned to it are unaffected.`,
          }),
        },
      }}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} sub-branch${rows.length === 1 ? '' : 'es'}?`,
            description:
              'These sub-branches will be soft-deleted. This can be reverted only by restoring from a backup.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} sub-branch${rows.length === 1 ? '' : 'es'}?`,
            description: isActive
              ? 'They will become selectable again on the user form.'
              : 'They will be hidden from new selections but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listSubBranches(q),
        filename: 'sub-branches',
        canExport: canExportFinance,
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Branch', value: (r) => r.branch?.name ?? '' },
          { header: 'Address', value: (r) => r.address ?? '' },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Legacy ID', value: (r) => r.legacyId ?? '' },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search by name or code (min 3 characters)',
        emptyTitle: 'No sub-branches match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load sub-branches',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
